import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import { startCase } from 'lodash';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useHistory } from 'react-router-dom';

import Collection from 'Views/Components/Collection/Collection';
import { ICollectionHeaderProps } from 'Views/Components/Collection/CollectionHeaders';
import { IUser } from 'Views/Components/CRUD/UserList';
import Chip from 'Views/Components/Chip/Chip';

import { useOnMount } from 'Hooks/useMounted';

import { UserEntities, userModelDisplayNameMap } from 'Models/Enums';

import { getAllUsers } from 'Queries/REST/Accounts/AccountRequests';

import alertToast from 'Util/ToastifyUtils';
import { getStatusForUser } from 'Util/UserUtils';
import { Conditions } from 'Util/QueryHelpers';
import { hasAnyRole } from 'Util/SecurityUtils';
import useStore from 'Hooks/useStore';

const PAGE_SIZE = 10;

const headers: Array<ICollectionHeaderProps<IUser>> = [
	{
		name: 'lastName',
		displayName: 'Last name',
	},
	{
		name: 'firstName',
		displayName: 'First name',
	},
	{
		name: 'email',
		displayName: 'Email',
	},
	{
		name: 'discriminator',
		displayName: 'Role',
		transformItem: user => userModelDisplayNameMap[user.discriminator as UserEntities],
	},
];

interface IStaffList {
	searchTerm?: string;
	filters: Conditions,
}

const StaffList: React.FC<IStaffList> = ({ searchTerm = '', filters }) => {
	const [animationRef] = useAutoAnimate();
	const history = useHistory();
	const store = useStore();

	const [page, setPage] = useState(0);
	const [data, setData] = useState<IUser[]>([]);
	const [totalRecordCount, setTotalRecordCount] = useState<number>(0);

	const resetPage = () => setPage(0);

	useEffect(
		() => resetPage(),
		[searchTerm, filters],
	);

	useOnMount(
		(isMounted, abortSignals) => {
			const searchQuery = `%${searchTerm}%`;

			return getAllUsers(
				abortSignals,
				{
					PageNo: page + 1, // page numer is one-indexed on serverside
					PageSize: PAGE_SIZE,
				},
				[
					[
						{
							path: 'discriminator',
							comparison: 'NotIn',
							value: ['UserEntity'],
						},
					],
					[
						{
							value: [searchQuery],
							comparison: 'Like',
							path: 'email',
							case: 'InvariantCultureIgnoreCase',
						},
						{
							value: [searchQuery],
							comparison: 'Like',
							path: 'FirstName',
							case: 'InvariantCultureIgnoreCase',
						},
						{
							value: [searchQuery],
							comparison: 'Like',
							path: 'LastName',
							case: 'InvariantCultureIgnoreCase',
						},
					],
					...filters,
				],
				[
					{
						path: 'lockoutEnd',
						descending: true,
					},
					{
						path: 'emailConfirmed',
						descending: true,
					},
					{
						path: 'lastName',
						descending: false,
					},
				],
			).then(
				response => {
					if (isMounted()) {
						setData(response.data.users);
						setTotalRecordCount(response.data.countUsers);
					}
				},
			).catch(err => {
				alertToast('Failed to retrieve Staff', 'error');
			});
		},
		[page, searchTerm, filters],
	);

	const customisedHeaders = useMemo(
		() => ([
			...headers,
			...(
				hasAnyRole(store, ['Admin', 'InstitutionAdmin'])
					? [{
						name: 'status',
						displayName: 'Status',
						transformItem: (user: IUser) => {
							const lowerCaseStatus = getStatusForUser(user).toLowerCase();
							const label = startCase(lowerCaseStatus);
							return (
								<Chip chipLabel={label} className={`user-status ${lowerCaseStatus}`} />
							);
						},
					}]
					: []
			),
		]),
		[],
	);

	return (
		<Collection<IUser>
			className="staff-list"
			ariaLabel="Staff list"
			headers={customisedHeaders}
			collection={data}
			onPageChange={newPageNumber => setPage(newPageNumber)}
			pageNo={page}
			perPage={PAGE_SIZE}
			totalRecords={totalRecordCount}
			idColumn="id"
			onRowClick={extendedStaffEntity => {
				// go to view details page
				history.push(`/users/staff/${extendedStaffEntity.discriminator}/${extendedStaffEntity.id}`);
			}}
			noDataMessage="No staff found"
			animationRef={animationRef}
			actionsMore={[
				{
					icon: 'edit',
					iconPos: 'icon-left',
					label: 'Edit',
					onEntityClick: (event, extendedStaffEntity) => {
						event.event.stopPropagation();
						// go to edit page
						history.push(
							`/users/staff/edit/${extendedStaffEntity.discriminator}/${extendedStaffEntity.id}`,
						);
					},
				},
			]}
		/>
	);
};

export default StaffList;
